import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Chip,
  Avatar,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Image as ImageIcon, 
  Error as ErrorIcon,
  History as HistoryIcon,
  AutoAwesome as AutoAwesomeIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

const GenerationEvents = () => {
  const { t } = useLocalization();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { token } = useAuth();

  const handleImageClick = async (params) => {
    setImageLoading(true);
    try {
      let response;
      if (params.event_type === 'planet') {
        response = await axios.get(`/planets/image/${params.generated_entity_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        });
      } else {
        response = await axios.get(`/attachments/image/${params.generated_entity_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        });
      }
      
      const imageUrl = URL.createObjectURL(response.data);
      setSelectedImage(imageUrl);
    } catch (err) {
      console.error('Error loading image:', err);
      setError(t('failed_to_load_image'));
    } finally {
      setImageLoading(false);
    }
  };

  const handleCloseImage = () => {
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'planet': return 'primary';
      case 'attachment': return 'secondary';
      default: return 'default';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'planet': return <ImageIcon />;
      case 'attachment': return <AutoAwesomeIcon />;
      default: return <ImageIcon />;
    }
  };

  const columns = [
    { 
      field: 'generation_datetime', 
      headerName: t('date_column'), 
      flex: 2,
      minWidth: 200,
      valueGetter: (params) => new Date(params.row.generation_datetime).toLocaleString(),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ 
            width: 32, 
            height: 32, 
            mr: 2, 
            bgcolor: 'primary.main'
          }}>
            <HistoryIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'event_type', 
      headerName: t('type_column'), 
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        const type = params.row.event_type;
        return type === 'planet' ? t('event_type_planet') : t('event_type_attachment');
      },
      renderCell: (params) => (
        <Chip
          icon={getEventTypeIcon(params.row.event_type)}
          label={params.value}
          color={getEventTypeColor(params.row.event_type)}
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { 
      field: 'generation_type', 
      headerName: t('generation_column'), 
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        const type = params.row.generation_type;
        return type === 'api_generated' ? t('generation_type_api_generated') : t('generation_type_pregenerated_use');
      },
      renderCell: (params) => {
        const isPregenerated = params.row.generation_type === 'pregenerated_use';
        return (
          <Chip
            label={params.value}
            color={isPregenerated ? "success" : "info"}
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'prompt', 
      headerName: t('prompt_column'), 
      flex: 3,
      minWidth: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography 
            noWrap 
            sx={{ 
              fontWeight: 500,
              maxWidth: 350
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    { 
      field: 'generated_image_path', 
      headerName: t('image_column'), 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <Tooltip title={t('show_image')}>
            <IconButton 
              size="small" 
              onClick={() => handleImageClick(params.row)}
              disabled={imageLoading}
              sx={{ 
                color: 'primary.main',
                '&:hover': { 
                  bgcolor: 'primary.light',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    },
    { 
      field: 'token_cost', 
      headerName: t('token_cost_column'), 
      flex: 1,
      minWidth: 150, 
      type: 'number',
      valueGetter: (params) => params.row.token_cost || 0,
      renderCell: (params) => (
        <Chip
          label={params.value === 0 ? t('free') : params.value}
          color="success"
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      )
    }
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('/events', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setEvents(response.data);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(t('failed_to_load_generation_events'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [token]);

  // Cleanup image URL when component unmounts
  useEffect(() => {
    return () => {
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
      }
    };
  }, [selectedImage]);

  if (!token) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('no_token_available_please_login')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalTokenCost = events.reduce((sum, event) => sum + (event.token_cost || 0), 0);
  const planetEvents = events.filter(event => event.event_type === 'planet').length;
  const attachmentEvents = events.filter(event => event.event_type === 'attachment').length;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <HistoryIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('generation_events')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('track_image_generation_events')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <HistoryIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {events.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <ImageIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {planetEvents}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('planet_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {attachmentEvents}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('attachment_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <MoneyIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {totalTokenCost}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_tokens_used')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('event_history')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('view_all_generation_events')}
              </Typography>
            </Box>
          </Box>
          
          <Paper sx={{ height: 600, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <DataGrid
              rows={events}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.event_id}
              disableSelectionOnClick
              autoWidth
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: 'text.secondary',
                    fontSize: '1rem',
                    fontWeight: 500
                  }}>
                    {t('no_events_found')}
                  </Box>
                )
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-columnHeader': {
                  flex: 1,
                },
                '& .MuiDataGrid-cell': {
                  flex: 1,
                },
              }}
            />
          </Paper>
        </CardContent>
      </Card>

      {/* Image Dialog */}
      <Dialog 
        open={!!selectedImage} 
        onClose={handleCloseImage}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {imageLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={60} />
            </Box>
          ) : (
            <img 
              src={selectedImage} 
              alt={t('generated')} 
              style={{ 
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '12px'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GenerationEvents; 